import moment from 'moment';
import React, { JSX } from 'react';

import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import { Col, Cols, spacing, Text, usePublicaTranslation } from '~/src/dash/publicaUI';
import { auctionInsightsTimeRanges, ITimeRange, TimeRange } from '~/src/dash/utils/dateTimeRanges';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';

interface IProps {
  dealStartDate: string;
  dealEndDate: string;
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  isDisabled?: boolean;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  loadState: LoadingState;
  realTimeRange: ITimeRange;
  handleToggleRealTimeRange: (rangeKey: TimeRange) => void;
  isRealtimeOnly: boolean;
  handleToggleRealtimeOnly: () => void;
}

export const DealAuctionInsightsFilters = ({
  handleApplyDatesChange,
  dealStartDate,
  dealEndDate,
  calStartDate,
  calEndDate,
  isDisabled = false,
  loadState,
  realTimeRange,
  handleToggleRealTimeRange,
  isRealtimeOnly,
  handleToggleRealtimeOnly,
}: IProps): JSX.Element => {
  const { t } = usePublicaTranslation();
  return (
    <Cols
      gap={spacing.large}
      style={{ justifyContent: 'flex-start', alignItems: 'center', height: '48px' }}
    >
      <Col>
        <PillSwitcherV2
          isDisabled={loadState === LoadingStateLoading}
          size='smallExtraLong'
          options={[
            {
              value: 'Custom Date',
              tKey: 'CustomDate',
              label: 'Custom Date',
              isSelected: !isRealtimeOnly,
              key: 'customDate',
            },
            {
              value: 'Real-Time (L24 hrs)',
              label: 'Real-Time (L24 hrs)',
              tKey: 'RealTimeToggleText',
              isSelected: isRealtimeOnly,
              key: 'realTime',
            },
          ]}
          onChange={() => {
            handleToggleRealtimeOnly();
          }}
          tooltipText={isRealtimeOnly ? t('RealTimeToggleTooltipText') : t('CustomDateTooltipText')}
        />
      </Col>
      {!isRealtimeOnly ? (
        <>
          <Col>
            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <IconV2 name='calendar' small />
              </Col>
              <Col>
                <Text small bold tKey='Period' />
              </Col>
            </Cols>
          </Col>
          {/* Calendar */}
          <Col>
            <CalendarV2
              calStartDate={calStartDate}
              calEndDate={calEndDate}
              handleApplyDatesChange={handleApplyDatesChange}
              isDisabled={isDisabled || loadState === LoadingStateLoading}
              uniqueID='insightsCal'
              minDate={dealStartDate ? moment(dealStartDate) : undefined}
              maxDate={
                dealEndDate &&
                !dealEndDate.startsWith('0000') &&
                moment(dealEndDate).isBefore(moment())
                  ? moment(dealEndDate)
                  : moment().endOf('day')
              }
              customInitialVisibleMonth={() => calEndDate.clone().subtract(1, 'month')}
            />
          </Col>
        </>
      ) : (
        <>
          <Col>
            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <IconV2 name='clock' small />
              </Col>
              <Col>
                <Text small bold tKey='TimeRange' />
              </Col>
            </Cols>
          </Col>
          <Col>
            <DateTimeRangeToggles
              isDisabled={loadState === LoadingStateLoading}
              ranges={auctionInsightsTimeRanges}
              activeKey={realTimeRange.key}
              onClick={(rangeKey: TimeRange) => {
                handleToggleRealTimeRange(rangeKey);
              }}
            />
          </Col>
        </>
      )}
    </Cols>
  );
};
